import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowRight, Calculator, CheckCircle2, Gauge, Globe, Landmark, ShieldCheck, Zap } from 'lucide-react';
import { useApp } from '../context/AppContext';
import useIsMobile from '../hooks/useIsMobile';
import { useTranslation } from 'react-i18next';

const fmt = (n) => new Intl.NumberFormat('it-IT', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
}).format(n || 0);

function ResultRow({ label, value }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', gap:'1rem', padding:'0.7rem 0', borderBottom:'1px solid rgba(255,255,255,0.12)' }}>
      <span style={{ color:'rgba(255,255,255,0.72)' }}>{label}</span>
      <strong style={{ color:'white', textAlign:'right' }}>{value}</strong>
    </div>
  );
}

function extractMaxDistance(errorMessage) {
  const match = String(errorMessage || '').match(/range massimo di\s+(\d+)m|max range\s+(\d+)m/i);
  if (!match) return 0;
  return Number(match[1] || match[2] || 0);
}

export default function CargoDroneCalculatorLanding() {
  const navigate = useNavigate();
  const { calculateOpex, trackScenario, language, setLanguage } = useApp();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const privacyPolicyUrl = 'https://flyingbasket.com/privacy-policy';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [result, setResult] = useState(null);
  const [simCount, setSimCount] = useState(() => parseInt(sessionStorage.getItem('fb_sim_count') || '0', 10));
  const [gateOpen, setGateOpen] = useState(false);
  const [form, setForm] = useState({
    droneModel: 'FB3',
    payloadKg: '30',
    distance: '3000',
    altitude: '150'
  });

  const languages = [
    { code: 'en', label: 'EN', flag: '🇬🇧' },
    { code: 'it', label: 'IT', flag: '🇮🇹' },
    { code: 'fr', label: 'FR', flag: '🇫🇷' },
    { code: 'es', label: 'ES', flag: '🇪🇸' },
    { code: 'de', label: 'DE', flag: '🇩🇪' },
  ];

  const lbsValue = useMemo(() => ((Number(form.payloadKg) || 0) * 2.20462).toFixed(1), [form.payloadKg]);
  const milesValue = useMemo(() => (((Number(form.distance) || 0) / 1000) * 0.621371).toFixed(2), [form.distance]);

  const setField = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));

  const buildTrackingPayload = () => ({
    workHours: 8,
    numPeople: 2,
    costPerPerson: 0,
    payloadKg: Math.max(1, Number(form.payloadKg) || 0),
    distance: Math.max(1, Number(form.distance) || 0),
    altitude: Math.max(0, Number(form.altitude) || 0),
    droneModel: form.droneModel,
    numDrones: 1,
    availableKw: 10,
    electricityCost: 0.25,
    calculatorType: 'single_route'
  });

  const trackLandingScenario = async (results, dailyCost = null) => {
    try {
      await trackScenario({
        name: `Landing single_route ${new Date().toLocaleString('it-IT')}`,
        scenarioType: 'single_route',
        inputParams: buildTrackingPayload(),
        results,
        dailyCost,
        roiMonths: null,
        source: 'landing'
      });
    } catch (trackError) {
      console.error('Landing scenario tracking failed', trackError);
    }
  };

  const runCalculation = async (e) => {
    e.preventDefault();
    if (simCount >= 2) {
      setGateOpen(true);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = buildTrackingPayload();
      const response = await calculateOpex('single-route', payload);
      const trackedResult = {
        ...response,
        feasible: true,
        maxFeasibleDistanceMeters: response?.operations?.maxFeasibleDistanceMeters || 0
      };
      setResult(trackedResult);
      await trackLandingScenario(trackedResult, response?.summary?.totalCost ?? null);
      setModalOpen(true);
    } catch (err) {
      const data = err?.response?.data || {};
      const maxFeasibleDistanceMeters =
        data.maxFeasibleDistanceMeters
        || data.operations?.maxFeasibleDistanceMeters
        || extractMaxDistance(data.error)
        || 0;
      setError(data.error || 'Errore nel calcolo. Riprova.');
      const trackedResult = {
        feasible: false,
        error: data.error || 'Tratta non fattibile.',
        maxFeasibleDistanceMeters,
        operations: {
          maxFeasibleDistanceMeters
        }
      };
      setResult(trackedResult);
      await trackLandingScenario(trackedResult, null);
      setModalOpen(true);
    } finally {
      setLoading(false);
      const newCount = simCount + 1;
      setSimCount(newCount);
      sessionStorage.setItem('fb_sim_count', String(newCount));
    }
  };

  const bureaucraticCards = [
    {
      icon: ShieldCheck,
      title: t('landing.cards.eu.title'),
      body: t('landing.cards.eu.body')
    },
    {
      icon: Landmark,
      title: t('landing.cards.us.title'),
      body: t('landing.cards.us.body')
    },
    {
      icon: Globe,
      title: t('landing.cards.business.title'),
      body: t('landing.cards.business.body')
    }
  ];

  return (
    <div style={{ minHeight:'100vh', background:'linear-gradient(180deg, #f7f9ff 0%, #eef3ff 100%)' }}>
      <div style={{ maxWidth:1180, margin:'0 auto', padding:isMobile ? '1rem' : '1.5rem 1.5rem 3rem' }}>
        <div style={{ display:'flex', flexDirection:isMobile ? 'column' : 'row', justifyContent:'space-between', alignItems:isMobile ? 'stretch' : 'center', gap:'1rem', marginBottom:isMobile ? '1.25rem' : '2rem' }}>
          <img src="/images/drones/logo.png" alt="FlyingBasket" style={{ height:isMobile ? 34 : 42, width:'auto', objectFit:'contain' }} />
          <div style={{ display:'flex', flexDirection:isMobile ? 'column' : 'row', gap:'0.75rem', flexWrap:'wrap', justifyContent:'flex-end', alignItems:isMobile ? 'stretch' : 'center' }}>
            <div
              style={{
                display:'flex', alignItems:'center', gap:'0.35rem',
                background:'var(--surface-low)', border:'1px solid var(--surface-high)',
                borderRadius:12, padding:'0.25rem',
                overflowX:isMobile ? 'auto' : 'visible',
                WebkitOverflowScrolling:'touch',
                scrollbarWidth:'none'
              }}
            >
              {languages.map(({ code, label, flag }) => (
                <button
                  key={code}
                  onClick={() => setLanguage(code)}
                  style={{
                    display:'flex', alignItems:'center', justifyContent:'center', gap:isMobile ? 0 : '0.35rem',
                    background:language === code ? 'white' : 'transparent',
                    border:'none', borderRadius:8, padding:isMobile ? '0.5rem 0.55rem' : '0.35rem 0.5rem',
                    fontSize:'0.78rem', fontWeight:700, cursor:'pointer',
                    color:language === code ? 'var(--primary)' : 'var(--on-surface)',
                    minWidth:isMobile ? 44 : 'auto',
                    flexShrink:0
                  }}
                  aria-label={label}
                  title={label}
                >
                  <span style={{ fontSize:'1rem' }}>{flag}</span>
                  {!isMobile && label}
                </button>
              ))}
            </div>
            <div style={{ display:'flex', gap:'0.65rem', width:isMobile ? '100%' : 'auto', flexDirection:isMobile ? 'row' : 'row' }}>
              <button className="btn-secondary" onClick={() => navigate('/login?mode=login')} style={{ flex:isMobile ? 1 : 'none', justifyContent:'center' }}>
                {t('landing.cta.login')}
              </button>
              <button className="btn-primary" onClick={() => navigate('/login?mode=register')} style={{ flex:isMobile ? 1 : 'none', justifyContent:'center' }}>
                {t('landing.cta.create')}
              </button>
            </div>
          </div>
        </div>

        <section style={{ display:'grid', gridTemplateColumns:isMobile ? '1fr' : '1.1fr 0.9fr', gap:isMobile ? '1rem' : '2rem', alignItems:'stretch', marginBottom:isMobile ? '1.5rem' : '2.5rem' }}>
          <div
            className="card"
            style={{
              backgroundImage:'linear-gradient(145deg, rgba(22,48,111,0.84) 0%, rgba(39,71,168,0.76) 62%, rgba(52,89,205,0.8) 100%), url(/images/drones/login-image.jpg)',
              backgroundSize:'cover',
              backgroundPosition:'center',
              color:'white',
              padding:isMobile ? '1.25rem' : '2rem',
              position:'relative',
              overflow:'hidden'
            }}
          >
            <div style={{ position:'relative', zIndex:1 }}>
            <div style={{ display:'inline-flex', alignItems:'center', gap:'0.5rem', background:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.18)', borderRadius:999, padding:'0.4rem 0.8rem', fontSize:'0.75rem', fontWeight:700, marginBottom:'1rem' }}>
              <Gauge size={14} /> {t('landing.badge')}
            </div>
            <h1 style={{ fontFamily:'var(--font-head)', fontSize:isMobile ? '2rem' : '3.15rem', lineHeight:1.02, fontWeight:800, maxWidth:700 }}>
              {t('landing.hero.title')}
            </h1>
            <p style={{ marginTop:'1rem', fontSize:isMobile ? '0.98rem' : '1.08rem', lineHeight:1.7, color:'rgba(255,255,255,0.92)', maxWidth:720 }}>
              {t('landing.hero.subtitle')}
            </p>
            <div style={{ display:'grid', gridTemplateColumns:isMobile ? '1fr' : 'repeat(3, 1fr)', gap:'0.85rem', marginTop:'1.5rem' }}>
              {[
                t('landing.hero.points.one'),
                t('landing.hero.points.two'),
                t('landing.hero.points.three')
              ].map((item) => (
                <div key={item} style={{ display:'flex', gap:'0.65rem', alignItems:'flex-start', background:'rgba(255,255,255,0.1)', borderRadius:14, padding:'0.9rem' }}>
                  <CheckCircle2 size={18} style={{ flexShrink:0, marginTop:2 }} />
                  <span style={{ fontSize:'0.9rem', lineHeight:1.45 }}>{item}</span>
                </div>
              ))}
            </div>
            </div>
          </div>

          <div className="card" style={{ padding:isMobile ? '1.1rem' : '1.5rem' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'0.6rem', color:'var(--primary)', marginBottom:'0.85rem' }}>
              <Calculator size={18} />
              <div style={{ fontFamily:'var(--font-head)', fontWeight:800, fontSize:'1.15rem' }}>{t('landing.calculator.title')}</div>
            </div>
            <p style={{ color:'var(--on-surface-muted)', lineHeight:1.6, fontSize:'0.92rem', marginBottom:'1.1rem' }}>
              {t('landing.calculator.subtitle')}
            </p>

            {error && (
              <div style={{ marginBottom:'1rem', background:'#fee2e2', color:'#b91c1c', padding:'0.85rem 1rem', borderRadius:12, fontSize:'0.9rem', lineHeight:1.5 }}>
                {error}
              </div>
            )}

            <form onSubmit={runCalculation}>
              <div style={{ display:'grid', gridTemplateColumns:isMobile ? '1fr' : '1fr 1fr', gap:'0.9rem' }}>
                <div>
                  <label className="label">{t('landing.form.droneModel')}</label>
                  <select className="input" value={form.droneModel} onChange={(e) => setField('droneModel')(e.target.value)}>
                    <option value="FB3">{t('landing.form.models.fb3')}</option>
                    <option value="FB4">{t('landing.form.models.fb4')}</option>
                  </select>
                </div>
                <div>
                  <label className="label">{t('landing.form.payload')}</label>
                  <input className="input" type="number" inputMode="decimal" min={1} step="any" value={form.payloadKg} onChange={(e) => setField('payloadKg')(e.target.value)} />
                  <div style={{ fontSize:'0.78rem', color:'var(--on-surface-muted)', marginTop:'0.25rem' }}>{lbsValue} lbs</div>
                </div>
                <div>
                  <label className="label">{t('landing.form.distance')}</label>
                  <input className="input" type="number" inputMode="decimal" min={1} step="any" value={form.distance} onChange={(e) => setField('distance')(e.target.value)} />
                  <div style={{ fontSize:'0.78rem', color:'var(--on-surface-muted)', marginTop:'0.25rem' }}>{milesValue} mi</div>
                </div>
                <div>
                  <label className="label">{t('landing.form.altitude')}</label>
                  <input className="input" type="number" inputMode="decimal" min={0} step="any" value={form.altitude} onChange={(e) => setField('altitude')(e.target.value)} />
                </div>
              </div>

              <button className="btn-primary" type="submit" disabled={loading} style={{ width:'100%', justifyContent:'center', marginTop:'1rem', padding:'1rem' }}>
                {loading ? t('landing.form.calculating') : t('landing.form.calculate')}
              </button>
            </form>

            <p style={{ marginTop:'0.85rem', color:'var(--on-surface-muted)', fontSize:'0.78rem', lineHeight:1.5 }}>
              {t('landing.privacyConsent')}{' '}
              <a href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer" style={{ color:'var(--primary)', textDecoration:'none', fontWeight:700 }}>
                {t('common.privacyPolicy')}
              </a>
            </p>

            {simCount === 0 && (
              <p style={{ marginTop:'0.9rem', color:'var(--on-surface-muted)', fontSize:'0.8rem', lineHeight:1.5 }}>
                {t('landing.calculator.note')}
              </p>
            )}
            {simCount === 1 && (
              <p style={{ marginTop:'0.9rem', display:'flex', alignItems:'flex-start', gap:'0.4rem', fontSize:'0.82rem', lineHeight:1.5, color:'#166534', background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:10, padding:'0.6rem 0.75rem' }}>
                {t('landing.trial.nudge1')}
              </p>
            )}
            {simCount >= 2 && (
              <p style={{ marginTop:'0.9rem', display:'flex', alignItems:'flex-start', gap:'0.4rem', fontSize:'0.82rem', lineHeight:1.5, color:'#92400e', background:'#fffbeb', border:'1px solid #fde68a', borderRadius:10, padding:'0.6rem 0.75rem' }}>
                {t('landing.trial.nudge2')}
              </p>
            )}
          </div>
        </section>

        <section className="card" style={{ padding:isMobile ? '1rem' : '1.2rem', marginBottom:isMobile ? '1.5rem' : '2rem' }}>
          <div>
            <div style={{ fontSize:'0.78rem', fontWeight:700, color:'var(--primary)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'0.45rem' }}>
              {t('landing.video.eyebrow')}
            </div>
            <h2 style={{ fontFamily:'var(--font-head)', fontSize:isMobile ? '1.45rem' : '1.9rem', fontWeight:800, color:'var(--primary)', marginBottom:'0.75rem' }}>
              {t('landing.video.title')}
            </h2>
            <p style={{ color:'var(--on-surface-muted)', lineHeight:1.7, marginBottom:'1rem' }}>
              {t('landing.video.body')}
            </p>
            <div style={{ borderRadius:18, overflow:'hidden', boxShadow:'0 18px 50px rgba(25, 48, 110, 0.12)', aspectRatio:'16 / 9', background:'#0f172a' }}>
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/V_6Cp9PQ1xs"
                title="FlyingBasket cargo drone video"
                style={{ border:0, width:'100%', height:'100%' }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
          </div>
        </section>

        <section className="card" style={{ marginBottom:isMobile ? '1.5rem' : '2rem', padding:isMobile ? '1.1rem' : '1.5rem' }}>
          <div style={{ maxWidth:860 }}>
            <div style={{ fontSize:'0.78rem', fontWeight:700, color:'var(--primary)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'0.45rem' }}>
              {t('landing.audience.eyebrow')}
            </div>
            <h2 style={{ fontFamily:'var(--font-head)', fontSize:isMobile ? '1.5rem' : '2rem', fontWeight:800, color:'var(--primary)', marginBottom:'0.75rem' }}>
              {t('landing.audience.title')}
            </h2>
            <p style={{ color:'var(--on-surface-muted)', lineHeight:1.75 }}>
              {t('landing.audience.body')}
            </p>
          </div>
        </section>

        <section style={{ display:'grid', gridTemplateColumns:isMobile ? '1fr' : 'repeat(3, 1fr)', gap:'1rem', marginBottom:isMobile ? '1.5rem' : '2rem' }}>
          {bureaucraticCards.map(({ icon: Icon, title, body }) => (
            <div key={title} className="card" style={{ padding:isMobile ? '1rem' : '1.25rem', background:'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(241,246,255,0.98) 100%)' }}>
              <div style={{ width:42, height:42, borderRadius:12, background:'var(--teal-light)', color:'var(--primary)', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:'0.9rem' }}>
                <Icon size={20} />
              </div>
              <h3 style={{ fontFamily:'var(--font-head)', fontWeight:800, fontSize:'1.1rem', color:'var(--primary)', marginBottom:'0.6rem' }}>
                {title}
              </h3>
              <p style={{ color:'var(--on-surface-muted)', lineHeight:1.7, fontSize:'0.94rem' }}>
                {body}
              </p>
            </div>
          ))}
        </section>

        <section className="card" style={{ background:'linear-gradient(135deg, #ffffff 0%, #eef3ff 100%)', padding:isMobile ? '1.1rem' : '1.5rem' }}>
          <div style={{ display:'grid', gridTemplateColumns:isMobile ? '1fr' : '1.2fr 0.8fr', gap:'1.2rem', alignItems:'center' }}>
            <div>
              <h2 style={{ fontFamily:'var(--font-head)', fontSize:isMobile ? '1.45rem' : '2rem', fontWeight:800, color:'var(--primary)', marginBottom:'0.75rem' }}>
                {t('landing.bottom.title')}
              </h2>
              <p style={{ color:'var(--on-surface-muted)', lineHeight:1.75 }}>
                {t('landing.bottom.body')}
              </p>
              <p style={{ color:'var(--on-surface-muted)', lineHeight:1.75, marginTop:'0.85rem' }}>
                {t('landing.bottom.contactLine')}
              </p>
              <div style={{ marginTop:'0.9rem', display:'flex', flexDirection:isMobile ? 'column' : 'row', justifyContent:'center', alignItems:'center', gap:isMobile ? '0.45rem' : '1.25rem', color:'var(--on-surface)', fontSize:'0.95rem' }}>
                <a href="tel:+3904711832430" style={{ color:'var(--primary)', textDecoration:'none', fontWeight:800 }}>
                  +39 0471 183 24 30
                </a>
                {!isMobile && <span style={{ color:'var(--on-surface-muted)' }}>•</span>}
                <a href="mailto:info@flyingbasket.com" style={{ color:'var(--primary)', textDecoration:'none', fontWeight:700 }}>
                  info@flyingbasket.com
                </a>
              </div>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
              <button className="btn-primary" style={{ width:'100%', justifyContent:'center' }} onClick={() => navigate('/login?mode=register')}>
                {t('landing.cta.create')} <ArrowRight size={16} />
              </button>
              <button className="btn-secondary" style={{ width:'100%', justifyContent:'center' }} onClick={() => navigate('/login?mode=login')}>
                {t('landing.bottom.haveAccount')}
              </button>
            </div>
          </div>
        </section>

        <footer style={{ marginTop:isMobile ? '1.5rem' : '2rem', padding:'1rem 0 0', borderTop:'1px solid rgba(37, 99, 235, 0.12)', display:'flex', flexDirection:isMobile ? 'column' : 'row', justifyContent:'space-between', gap:'0.8rem', alignItems:isMobile ? 'flex-start' : 'center', color:'var(--on-surface-muted)', fontSize:'0.9rem' }}>
          <div>FlyingBasket</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:'1rem' }}>
            <a href="mailto:info@flyingbasket.com" style={{ color:'inherit', textDecoration:'none' }}>info@flyingbasket.com</a>
            <a href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer" style={{ color:'inherit', textDecoration:'none' }}>
              {t('common.privacyPolicy')}
            </a>
          </div>
        </footer>
      </div>

      {gateOpen && (
        <div style={{ position:'fixed', inset:0, background:'rgba(10,20,45,0.6)', display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem', zIndex:300 }}>
          <div style={{ width:'100%', maxWidth:520, background:'white', borderRadius:24, overflow:'hidden', boxShadow:'0 30px 80px rgba(15,26,58,0.3)' }}>
            <div style={{ background:'linear-gradient(145deg, #173171 0%, #2d4eb7 60%, #3d62d2 100%)', padding:isMobile ? '1.25rem' : '1.6rem' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'0.65rem', color:'white', marginBottom:'0.75rem' }}>
                <Zap size={22} />
                <h3 style={{ fontFamily:'var(--font-head)', fontWeight:800, fontSize:isMobile ? '1.2rem' : '1.45rem', margin:0 }}>
                  {t('landing.trial.gate.title')}
                </h3>
              </div>
              <div style={{ display:'inline-flex', alignItems:'center', gap:'0.4rem', background:'rgba(255,255,255,0.18)', border:'1px solid rgba(255,255,255,0.3)', borderRadius:999, padding:'0.3rem 0.85rem', fontSize:'0.78rem', fontWeight:800, color:'white', marginBottom:'0.9rem', letterSpacing:'0.02em' }}>
                ✓ {t('landing.trial.gate.pill')}
              </div>
              <p style={{ color:'rgba(255,255,255,0.88)', lineHeight:1.65, fontSize:'0.95rem', margin:0 }}>
                {t('landing.trial.gate.body')}
              </p>
            </div>
            <div style={{ padding:isMobile ? '1.1rem' : '1.4rem' }}>
              <ul style={{ listStyle:'none', margin:0, padding:0, display:'flex', flexDirection:'column', gap:'0.55rem', marginBottom:'1.25rem' }}>
                {['f1','f2','f3','f4','f5'].map((k) => (
                  <li key={k} style={{ display:'flex', alignItems:'center', gap:'0.6rem', fontSize:'0.92rem', color:'var(--on-surface)' }}>
                    <CheckCircle2 size={16} style={{ flexShrink:0, color:'#16a34a' }} />
                    {t(`landing.trial.gate.${k}`)}
                  </li>
                ))}
              </ul>
              <button className="btn-primary" style={{ width:'100%', justifyContent:'center', padding:'1rem', fontSize:'1rem' }} onClick={() => navigate('/login?mode=register')}>
                {t('landing.trial.gate.cta')} <ArrowRight size={16} />
              </button>
              <p style={{ textAlign:'center', fontSize:'0.78rem', color:'var(--on-surface-muted)', margin:'0.55rem 0 0.85rem' }}>
                {t('landing.trial.gate.ctaNote')}
              </p>
              <button className="btn-secondary" style={{ width:'100%', justifyContent:'center' }} onClick={() => navigate('/login?mode=login')}>
                {t('landing.trial.gate.login')}
              </button>
              <button onClick={() => setGateOpen(false)} style={{ marginTop:'0.75rem', width:'100%', background:'transparent', border:'none', color:'var(--on-surface-muted)', cursor:'pointer', fontSize:'0.87rem', padding:'0.4rem' }}>
                {t('landing.trial.gate.dismiss')}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalOpen && result && (
        <div style={{ position:'fixed', inset:0, background:'rgba(10,20,45,0.56)', display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem', zIndex:300 }}>
          <div style={{ width:'100%', maxWidth:640, borderRadius:24, overflow:'hidden', boxShadow:'0 30px 80px rgba(15,26,58,0.3)' }}>
            <div style={{ background:'linear-gradient(145deg, #173171 0%, #2d4eb7 60%, #3d62d2 100%)', padding:isMobile ? '1.25rem' : '1.6rem' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'0.7rem', marginBottom:'0.85rem', flexWrap:'wrap' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'0.7rem', color:'white' }}>
                  {result.feasible ? <CheckCircle2 size={22} /> : <AlertCircle size={22} />}
                  <h3 style={{ fontFamily:'var(--font-head)', fontWeight:800, fontSize:isMobile ? '1.25rem' : '1.5rem' }}>
                    {t('landing.modal.title')}
                  </h3>
                </div>
                <span style={{ fontSize:'0.72rem', fontWeight:700, background:'rgba(255,255,255,0.18)', border:'1px solid rgba(255,255,255,0.28)', borderRadius:999, padding:'0.25rem 0.65rem', color:'rgba(255,255,255,0.9)', whiteSpace:'nowrap' }}>
                  {simCount === 1 ? t('landing.trial.modalBadge1') : t('landing.trial.modalBadge2')}
                </span>
              </div>
              <p style={{ color:'rgba(255,255,255,0.8)', lineHeight:1.65, marginBottom:'1rem' }}>
                {result.feasible
                  ? t('landing.modal.feasibleText')
                  : t('landing.modal.notFeasibleText', {
                      payloadKg: Number(form.payloadKg) || 0,
                      maxDistance: result.maxFeasibleDistanceMeters || result.operations?.maxFeasibleDistanceMeters || 0
                    })}
              </p>

              {result.feasible ? (
                <>
                  <ResultRow label={t('landing.modal.rows.estimatedCost')} value={fmt(result.summary?.totalCost)} />
                  <ResultRow label={t('landing.modal.rows.estimatedFlightTime')} value={`${(result.operations?.totalFlightHours || 0).toFixed(2)} h`} />
                  <ResultRow label={t('landing.modal.rows.costPerKg')} value={fmt(result.summary?.costPerKg)} />
                  <ResultRow label={t('landing.modal.rows.maxDistance')} value={`${result.maxFeasibleDistanceMeters || result.operations?.maxFeasibleDistanceMeters || 0} m`} />
                  <ResultRow label={t('landing.modal.rows.estimatedEnergy')} value={`${(result.operations?.totalEnergyUsed || 0).toFixed(2)} kWh`} />
                </>
              ) : (
                <>
                  <ResultRow label={t('landing.modal.rows.outcome')} value={t('landing.modal.rows.notFeasibleValue')} />
                  <ResultRow label={t('landing.modal.rows.payload')} value={`${Number(form.payloadKg) || 0} kg`} />
                  <ResultRow label={t('landing.modal.rows.requestedDistance')} value={`${Number(form.distance) || 0} m`} />
                  <ResultRow label={t('landing.modal.rows.maxPossibleDistance')} value={`${result.maxFeasibleDistanceMeters || result.operations?.maxFeasibleDistanceMeters || 0} m`} />
                </>
              )}

              <div style={{ display:'flex', flexDirection:isMobile ? 'column' : 'row', gap:'0.75rem', marginTop:'1.2rem' }}>
                <button className="btn-primary" style={{ flex:1, justifyContent:'center', background:'white', color:'var(--primary)' }} onClick={() => navigate('/login?mode=register')}>
                  {t('landing.cta.create')}
                </button>
                <button className="btn-secondary" style={{ flex:1, justifyContent:'center', background:'rgba(255,255,255,0.14)', color:'white' }} onClick={() => navigate('/login?mode=login')}>
                  {t('landing.cta.login')}
                </button>
              </div>
              <button onClick={() => setModalOpen(false)} style={{ marginTop:'0.9rem', width:'100%', background:'transparent', border:'none', color:'rgba(255,255,255,0.76)', cursor:'pointer', fontSize:'0.9rem' }}>
                {t('landing.modal.close')}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
